import { filterMapForUpdatedValues } from "@src/features/staffing_request_details/utils/propose_allocations_utils";
import {
  Allocation,
  AllocationRowData,
  ResourcePlanTableRowData,
  RoleAllocationDetailsDTO,
  RoleRequest,
  UpdateRoleRequestPayload,
} from "@src/types/role_request_types";
import { isValueValid } from "@src/utils/edit_allocation_utils";
import dayjs, { Dayjs } from "dayjs";
import { sortFields } from "@src/utils/helper";

/**
 * Maps an array of RoleRequest objects to ResourcePlanTableRowData,
 * converting allocationDetails into a Map keyed by "month-year".
 *
 * @param {RoleRequest[]} roleRequestData - The array of role requests.
 * @param {string} jointVenturePartner - The joint venture partner for the project.
 * @returns {ResourcePlanTableRowData[]}  - An array of mapped resource plan table row data.
 */
export function mapRoleRequestData(
  roleRequestData: RoleRequest[],
  jointVenturePartner?: string
): ResourcePlanTableRowData[] {
  if (!roleRequestData) return [];

  return roleRequestData
    ?.map((roleRequest: RoleRequest) => {
      const allocationMap = new Map<string, AllocationRowData>();
      roleRequest.allocationDetails?.forEach((allocation: Allocation) => {
        const mapKey = dayjs(allocation.date).format("M-YYYY");

        allocationMap.set(mapKey, {
          percentage: allocation.requiredPercentage,
        });
      });

      const { allocationDetails, ...resourcePlanTableRequest } = roleRequest;
      return {
        ...resourcePlanTableRequest,
        allocations: allocationMap,
        jointVenturePartner,
      } as ResourcePlanTableRowData;
    })
    ?.sort((a, b) =>
      sortFields(b, a, "isOutdated", "actionRequired", "isUpdated")
    );
}

/**
 * Filters and maps the allocation changes for a given request.
 *
 * @param {ResourcePlanTableRowData} request - The resource plan table row data containing allocation changes.
 * @returns {RoleAllocationDetailsDTO[]}     - An array of RoleAllocationDetailsDTO representing the new allocations.
 */
export function getAllocationChangesForRequest(
  request: ResourcePlanTableRowData
): RoleAllocationDetailsDTO[] {
  const newAllocations: RoleAllocationDetailsDTO[] = [];
  const changedAllocations: Map<string, AllocationRowData> =
    filterMapForUpdatedValues(request.allocations);

  // Iterate over the allocations and extract valid allocation changes
  for (const [dateKey, allocation] of changedAllocations) {
    // Abort if updated percentage is not valid
    if (!isValueValid(allocation.updatedPercentage)) return [];

    const [month, year] = dateKey.split("-");
    const allocationDate: Dayjs = dayjs(`${year}-${month}-01`);
    newAllocations.push({
      year: allocationDate.year(),
      month: allocationDate.month() + 1,
      date: allocationDate.utc(true).format(),
      requiredPercentage: allocation.updatedPercentage,
    } as RoleAllocationDetailsDTO);
  }
  return newAllocations;
}

/**
 * Creates a payload for updating a role request.
 *
 * @param selectedRequest                              - The selected role request object.
 * @param {RoleAllocationDetailsDTO[]} newAllocations  - The new allocations to be included in the payload.
 * @param {boolean} isDraft                            - Indicates if the request is a draft.
 *
 * @returns {RoleAllocationDetailsDTO[]}               - The payload object for updating the role request.
 */
export function createRequestPayload(
  selectedRequest: ResourcePlanTableRowData,
  newAllocations: RoleAllocationDetailsDTO[],
  isDraft: boolean
): UpdateRoleRequestPayload {
  return {
    isJointVenture: selectedRequest.jointVenture,
    isDraft: isDraft,
    scope: selectedRequest.scope,
    specificRole: selectedRequest.specificRole,
    projectIdentifier: selectedRequest.projectIdentifier,
    allocations: newAllocations,
  };
}
